import { BACKGROUND_PATTERN_ID } from '@/domain/selection/reservedSelectionIds';
import { FABRIC_CONTROL_COLOR } from '@/infrastructure/engine2d/engine2dConstants';
import { WORKING_ZONE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { classRegistry, Pattern, Rect } from 'fabric';
import type { TPatternArea } from '@/domain/print/TPatternArea';
import type { BasicTransformEvent, Canvas, FabricObject, TPointerEvent } from 'fabric';

type TBackgroundPatternServiceParams = {
    canvas: Canvas;
    getImageRenderHeight: () => number;
    getImageRenderWidth: () => number;
};

type TAreaListener = (area: TPatternArea) => void;

const FULL_AREA: TPatternArea = { height: 1, width: 1, x: 0, y: 0 };
const MIN_AREA_FRACTION = 0.05;

// A Rect under another type name: the export pipeline wipes the fill of every top-level 'Rect'
// (it is how it empties the working zone), which would blank the motif. Registered so the
// export's canvas clone can rebuild it.
class PatternRect extends Rect {
    static override type = 'PatternRect';
    declare id?: string;
}
classRegistry.setClass(PatternRect);

// The repeated motif of EDI-09: a rectangle filled with the tile image, laid just above the
// working zone (which carries the background colour) and under everything the customer adds.
// It covers the printable zone until the customer moves or resizes it (devis complémentaire n°2):
// it is selected, moved and resized like any other element (its handles, its context menu, its
// row in the layers), and the area it ends up in is reported back so the order keeps it. The
// rectangle is resized by width and height, never by scale, so the tiles keep their size under
// the handles.
class BackgroundPatternService {
    private readonly _areaListeners = new Set<TAreaListener>();
    private readonly _canvas: Canvas;
    private readonly _getImageRenderHeight: () => number;
    private readonly _getImageRenderWidth: () => number;
    // Two quick changes (a slider dragged) can finish out of order; only the last one lands.
    private _latestRequest = 0;
    private _tileImageUrl: string | null = null;

    constructor(params: TBackgroundPatternServiceParams) {
        this._canvas = params.canvas;
        this._getImageRenderHeight = params.getImageRenderHeight;
        this._getImageRenderWidth = params.getImageRenderWidth;
        this._canvas.on('object:moving', this._onTransforming);
        this._canvas.on('object:scaling', this._onTransforming);
        this._canvas.on('object:modified', this._onModified);
    }

    dispose(): void {
        this._canvas.off('object:moving', this._onTransforming);
        this._canvas.off('object:scaling', this._onTransforming);
        this._canvas.off('object:modified', this._onModified);
        this._areaListeners.clear();
    }

    onAreaChanged(listener: TAreaListener): () => void {
        this._areaListeners.add(listener);
        return () => this._areaListeners.delete(listener);
    }

    async set(tileImageUrl: string | null, area: TPatternArea = FULL_AREA, select = false): Promise<void> {
        this._latestRequest += 1;
        const request = this._latestRequest;
        const current = this._current();
        // Same tile: the customer only moved or resized it, there is nothing to rebuild.
        if (current && tileImageUrl && tileImageUrl === this._tileImageUrl) {
            this._place(current, area);
            this._canvas.requestRenderAll();
            return;
        }
        const wasSelected = current !== undefined && this._canvas.getActiveObject() === current;
        const tile = tileImageUrl ? await this._loadTile(tileImageUrl) : undefined;
        if (request !== this._latestRequest) {
            return;
        }
        this._removeCurrent();
        this._tileImageUrl = tile ? tileImageUrl : null;
        if (tile) {
            this._add(tile, area, select || wasSelected);
        }
        this._canvas.requestRenderAll();
    }

    private readonly _onTransforming = (event: BasicTransformEvent<TPointerEvent> & { target: FabricObject }): void => {
        const target = event.target as PatternRect;
        if (target.id !== BACKGROUND_PATTERN_ID) {
            return;
        }
        this._bakeScale(target);
        this._clampToZone(target);
    };

    private readonly _onModified = (event: { target: FabricObject }): void => {
        const target = event.target as PatternRect;
        if (target.id !== BACKGROUND_PATTERN_ID) {
            return;
        }
        this._bakeScale(target);
        this._clampToZone(target);
        const area = this._areaOf(target);
        this._areaListeners.forEach((listener) => listener(area));
    };

    private _add(tile: HTMLImageElement, area: TPatternArea, select: boolean): void {
        const zone = new PatternRect({
            cornerColor: FABRIC_CONTROL_COLOR,
            cornerStrokeColor: FABRIC_CONTROL_COLOR,
            fill: new Pattern({ repeat: 'repeat', source: tile }),
            lockRotation: true,
            objectCaching: false,
            strokeWidth: 0,
            transparentCorners: false,
        });
        zone.set({ id: BACKGROUND_PATTERN_ID });
        zone.setControlsVisibility({ mtr: false });
        this._place(zone, area);
        this._canvas.add(zone);
        const workingZone = this._canvas.getObjects().find((object) => object.get('name') === WORKING_ZONE_NAME);
        if (workingZone) {
            this._canvas.moveObjectTo(zone, this._canvas.getObjects().indexOf(workingZone) + 1);
        } else {
            this._canvas.sendObjectToBack(zone);
        }
        if (select) {
            this._canvas.setActiveObject(zone);
        }
    }

    private _place(zone: PatternRect, area: TPatternArea): void {
        const { height, width } = this._zoneSize();
        zone.set({
            height: area.height * height,
            left: area.x * width,
            scaleX: 1,
            scaleY: 1,
            top: area.y * height,
            width: area.width * width,
        });
        zone.setCoords();
    }

    private _areaOf(zone: PatternRect): TPatternArea {
        const { height, width } = this._zoneSize();
        return { height: zone.height / height, width: zone.width / width, x: zone.left / width, y: zone.top / height };
    }

    private _bakeScale(zone: PatternRect): void {
        if (zone.scaleX === 1 && zone.scaleY === 1) {
            return;
        }
        zone.set({ height: zone.height * zone.scaleY, scaleX: 1, scaleY: 1, width: zone.width * zone.scaleX });
    }

    // Inside the printable zone, and never smaller than a few tiles' worth of handles.
    private _clampToZone(zone: PatternRect): void {
        const { height, width } = this._zoneSize();
        const nextWidth = Math.min(Math.max(zone.width, MIN_AREA_FRACTION * width), width);
        const nextHeight = Math.min(Math.max(zone.height, MIN_AREA_FRACTION * height), height);
        zone.set({
            height: nextHeight,
            left: Math.min(Math.max(zone.left, 0), width - nextWidth),
            top: Math.min(Math.max(zone.top, 0), height - nextHeight),
            width: nextWidth,
        });
        zone.setCoords();
    }

    private _zoneSize(): { height: number; width: number } {
        return { height: Math.floor(this._getImageRenderHeight()), width: Math.floor(this._getImageRenderWidth()) };
    }

    private _current(): PatternRect | undefined {
        return this._canvas.getObjects().find((object) => (object as PatternRect).id === BACKGROUND_PATTERN_ID) as PatternRect | undefined;
    }

    private _removeCurrent(): void {
        this._canvas
            .getObjects()
            .filter((object) => (object as { id?: string }).id === BACKGROUND_PATTERN_ID)
            .forEach((object) => this._canvas.remove(object));
    }

    // Not through ImageLoaderManager: it keeps every URL it loads, and each colour and size of a
    // motif is a URL of its own.
    private _loadTile(tileImageUrl: string): Promise<HTMLImageElement | undefined> {
        return new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => {
                console.error('BackgroundPatternService: the motif tile could not be loaded');
                resolve(undefined);
            };
            image.src = tileImageUrl;
        });
    }
}

export { BackgroundPatternService };
